import React, { useState } from "react";
import { apiPost } from "@/lib/api";
import { Send, CheckCircle2 } from "lucide-react";
import ConsentMicrocopy from "@/components/ConsentMicrocopy";

/**
 * Reusable mini lead-capture form.
 * POSTs to /api/leads/partial with the given source tag.
 *
 * Props:
 * - source: string (e.g. "persona-doctors-high-earners", "newsletter", "article-iul-tax-free-retirement")
 * - title?: string
 * - subtitle?: string
 * - withName?: boolean (default true)
 * - withPhone?: boolean (default false)
 * - concernOptions?: Array<{value, label}> (if provided, renders a dropdown)
 * - context?: object (extra fields posted as source_context)
 */
export default function MiniLeadForm({
  source,
  title = "Get a personalized recommendation",
  subtitle = "We'll be in touch within 1 business day.",
  withName = true,
  withPhone = false,
  concernOptions,
  context = {},
  variant = "card", // "card" | "inline"
  testIdPrefix = "mini-lead",
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [concern, setConcern] = useState(concernOptions ? concernOptions[0]?.value : "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !/.+@.+\..+/.test(email)) {
      setError("Please enter a valid email.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await apiPost("/leads/partial", {
        email,
        source,
        source_context: {
          ...context,
          name: name || undefined,
          phone: phone || undefined,
          top_concern: concern || undefined,
        },
      });
      setSuccess(true);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div
        data-testid={`${testIdPrefix}-success`}
        className={`${variant === "card" ? "bg-white rounded-2xl border border-lw-gold/40 p-6 sm:p-7" : "bg-lw-ivory-warm rounded-xl p-5"} flex items-start gap-3`}
      >
        <CheckCircle2 size={22} className="text-lw-gold flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-serif-display text-lg text-lw-navy">Thank you.</p>
          <p className="text-sm text-lw-muted mt-1">{subtitle}</p>
        </div>
      </div>
    );
  }

  const cardCls =
    variant === "card"
      ? "bg-white rounded-2xl border border-lw-navy/10 p-6 sm:p-7"
      : "bg-lw-ivory-warm rounded-xl p-5";

  return (
    <form
      onSubmit={submit}
      data-testid={`${testIdPrefix}-form`}
      className={`${cardCls} space-y-3`}
    >
      <div>
        <h3 className="font-serif-display text-xl text-lw-navy">{title}</h3>
        <p className="text-sm text-lw-muted mt-1">{subtitle}</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {withName && (
          <input
            data-testid={`${testIdPrefix}-name`}
            type="text"
            placeholder="Your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-lg border border-lw-navy/15 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-lw-gold/40"
          />
        )}
        <input
          data-testid={`${testIdPrefix}-email`}
          type="email"
          required
          placeholder="Email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full rounded-lg border border-lw-navy/15 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-lw-gold/40"
        />
        {withPhone && (
          <input
            data-testid={`${testIdPrefix}-phone`}
            type="tel"
            placeholder="Phone (optional)"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="w-full rounded-lg border border-lw-navy/15 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-lw-gold/40"
          />
        )}
      </div>
      {concernOptions && (
        <select
          data-testid={`${testIdPrefix}-concern`}
          value={concern}
          onChange={(e) => setConcern(e.target.value)}
          className="w-full rounded-lg border border-lw-navy/15 px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-lw-gold/40"
        >
          {concernOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      )}
      {error && (
        <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {error}
        </p>
      )}
      <button
        type="submit"
        disabled={submitting}
        data-testid={`${testIdPrefix}-submit`}
        className="btn-gold w-full sm:w-auto justify-center disabled:opacity-60"
      >
        {submitting ? "Sending…" : "Send"} <Send size={16} />
      </button>
      <ConsentMicrocopy withPhone={withPhone} className="mt-1" />
    </form>
  );
}
